import { useCallback, useEffect, useState } from "react";
import Script from "next/script";
import { useRouter } from "next/router";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import { Button, Card, Input, ReadMoreWrapper } from "@chef/components";
import { BRAND_PATHS, config } from "@chef/constants";
import { useExtendedForm } from "@chef/hooks";
import { FacebookIcon, FacebookMessenger, Link } from "@chef/icons/small";
import {
  selectCompetitionDisclosureOpen,
  setCompetitionDisclosureOpen,
  showNotification,
  useBillingQuery,
  useSendTellAFriendSmsMutation,
} from "@chef/state-management";
import { LINKS } from "@chef/constants";
import { BlockContent } from "@chef/feature-sanity";
import { ShareButton } from "@chef/smart-components";
import { useTrack } from "@chef/state-management/hooks";

import {
  sendErrorNotification,
  shareReferralCodeViaFacebook,
  telephoneSchema,
} from "../../utils";

import { intl } from "./Share.Intl";

// FB api
declare const FB: {
  ui: (...args: any[]) => void;
  init: (...args: any[]) => void;
};

declare global {
  interface Window {
    fbAsyncInit: () => void;
    FB: any;
  }
}

export interface ShareProps {
  defaultBlockHeading: any;
  smsMessage: any;
  howItWorks: any;
}

type ISmsFormInput = {
  telephone: string;
  message: string;
};

export const Share = ({
  defaultBlockHeading,
  smsMessage,
  howItWorks,
}: ShareProps) => {
  const [isFBLoaded, setIsFBLoaded] = useState(false);

  const { data: billingQuery, isLoading: isBillingLoading } = useBillingQuery();

  const router = useRouter();
  const track = useTrack();
  const dispatch = useDispatch();

  const openDisclosure = useSelector(selectCompetitionDisclosureOpen);

  const [sendTellAFriendSms, { isLoading: isTellAFriendSmsLoading }] =
    useSendTellAFriendSmsMutation();

  useEffect(() => {
    if (openDisclosure) {
      dispatch(setCompetitionDisclosureOpen({ open: false }));
    }
  }, []);

  useEffect(() => {
    if (window.FB) {
      setIsFBLoaded(true);
    }
  }, []);

  const referralCode = billingQuery?.billing.couponCode;
  const referralLink = LINKS.REFERRAL_LINK(referralCode);

  const handleRecruitInvitationShared = (shareType: string) => {
    track("recruitInvitationShared", {
      affiliation: "Frontend process",
      share_url: referralLink,
      share_type: shareType,
    });
  };

  const { register, handleSubmit, reset } = useExtendedForm<ISmsFormInput>({
    resolver: yupResolver(telephoneSchema),
    mode: "all",
  });

  const redirectToSuccessPage = () => {
    router.push(BRAND_PATHS.TELL_A_FRIEND_CONFIRMATION_PAGE);
  };

  const handleShareFacebook = useCallback(() => {
    shareReferralCodeViaFacebook({
      method: "share",
      href: referralLink,
      FB,
    });
    handleRecruitInvitationShared("facebook");
  }, [redirectToSuccessPage, referralLink]);

  const handleSendSMS: SubmitHandler<ISmsFormInput> = async ({ telephone }) => {
    await sendTellAFriendSms({
      phoneNumber: telephone,
    }).unwrap();
    handleRecruitInvitationShared("sms");
    reset();

    redirectToSuccessPage();
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    handleRecruitInvitationShared("copy");
    showNotification({
      type: "success",
      message: intl.LINK_COPIED,
    });
  };

  const handleShareToMessenger = () => {
    try {
      if (
        localStorage.getItem("mobile") !== null ||
        navigator.userAgent.match(/Android/i) ||
        navigator.userAgent.match(/webOS/i) ||
        navigator.userAgent.match(/iPhone/i) ||
        navigator.userAgent.match(/iPad/i) ||
        navigator.userAgent.match(/iPod/i) ||
        navigator.userAgent.match(/BlackBerry/i) ||
        navigator.userAgent.match(/Windows Phone/i)
      ) {
        window.open(
          `fb-messenger://share?link=${encodeURIComponent(
            referralLink,
          )}&app_id=${encodeURIComponent(config.facebook.appId)}`,
        );
      } else {
        shareReferralCodeViaFacebook({
          method: "send",
          link: referralLink,
          description: referralLink,
          FB,
        });
      }
      handleRecruitInvitationShared("messenger");
    } catch (error) {
      console.error({ error });
      sendErrorNotification(intl.SHARE_ON_FACEBOOK_ERROR);
    }
  };

  return (
    <>
      <Script
        src={BRAND_PATHS.FACEBOOK_API_URL}
        nonce="DR0ZTzGb"
        strategy="lazyOnload"
        onLoad={() => {
          window.fbAsyncInit = function () {
            FB.init({
              appId: config.facebook.appId,
              autoLogAppEvents: true,
              xfbml: true,
              version: "v12.0",
            });
            setIsFBLoaded(true);
          };
        }}
      />

      <Card>
        {defaultBlockHeading}
        <form onSubmit={handleSubmit(handleSendSMS)}>
          <p className="my-4 text-sm">
            <strong>{intl.SEND_AS_SMS}</strong>
          </p>

          <Input
            {...register("telephone")}
            type="tel"
            label={intl.YOUR_FRIEND_PHONE_NUMBER}
            id="telephone"
            autoComplete="off"
            disabled={isTellAFriendSmsLoading}
            maxLength={intl.PHONE_NUMBER_DIGITS}
          />

          <Button
            full
            primary
            type="submit"
            className="mt-4 mb-6"
            loading={isBillingLoading}
          >
            {intl.SEND_SMS}
          </Button>
        </form>

        <ReadMoreWrapper
          moreButtonText={intl.SHOW_THE_FULL_TEXT}
          lessButtonText={intl.SHOW_LESS}
          background="background"
          innerClassName="text-xs font-bold text-primary text-left px-6 pb-4 underline"
          className="px-6 py-4"
          height="short"
        >
          <BlockContent body={smsMessage} prose={{ padding: "none" }} />
        </ReadMoreWrapper>

        <div className="mt-8">
          <p className="text-sm">
            <strong>{intl.OR_SHARE_THE_DICOUNT_VIA}</strong>
          </p>
          <div className="flex justify-around mt-4 align-items">
            <div className="flex flex-col items-center">
              <ShareButton
                href={referralLink}
                className="!w-12 !h-12 !bg-primary !text-white"
              />
              <p className="mt-3 text-xs">{intl.SHARE_LINK}</p>
            </div>

            <button onClick={handleCopyLink} className="flex flex-col">
              <div>
                <div className="flex items-center justify-center w-12 h-12 ml-2 rounded-full bg-primary">
                  <Link className="text-white " width={20} height={20} />
                </div>
                <p className="mt-3 text-xs">{intl.COPY_LINK}</p>
              </div>
            </button>

            <button
              onClick={handleShareToMessenger}
              className="flex flex-col"
              disabled={!isFBLoaded}
            >
              <div>
                <FacebookMessenger width={48} height={48} />
                <p className="mt-3 text-xs">Messenger</p>
              </div>
            </button>

            <button
              onClick={handleShareFacebook}
              className="flex flex-col"
              disabled={!isFBLoaded}
            >
              <div>
                <FacebookIcon width={48} height={48} />
                <p className="mt-3 text-xs">Facebook</p>
              </div>
            </button>
          </div>
        </div>

        {howItWorks}
      </Card>
    </>
  );
};
